import { Plus, Search, UserRound } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useVenueScope } from "../../app/venueScope";
import { formatDate } from "../../lib/format";
import {
  Card,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  PageHeader,
  StatusPill,
} from "../../ui/components";
import { ListPageSkeleton } from "../../ui/skeletons";
import { contactTypeLabels, useContacts } from "./queries";

export function ContactsPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const venue = useVenueScope();
  const contacts = useContacts(search, status, venue.selectedVenueId);

  if (contacts.isLoading)
    return (
      <ListPageSkeleton
        title="Contacts"
        description="Everyone you hold bookings and invoices for, with the organization each of them acts for."
        filterCount={2}
        columnCount={8}
      />
    );
  if (contacts.error) return <ErrorPanel error={contacts.error} />;
  const items = contacts.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Contacts"
        description="Everyone you hold bookings and invoices for, with the organization each of them acts for."
        actions={
          <Link className="button primary" to="/contacts/new">
            <Plus size={17} aria-hidden="true" /> Add contact
          </Link>
        }
      />
      <Card>
        <div className="filters">
          <Field label="Search contacts">
            <span className="inline">
              <Search size={18} aria-hidden="true" />
              <input
                type="search"
                aria-label="Search contacts"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Name, email, or organization"
              />
            </span>
          </Field>
          <Field label="Contact status">
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
              <option value="">Every status</option>
            </select>
          </Field>
        </div>
      </Card>
      <Card>
        {items.length ? (
          <DataTable
            caption="Contacts"
            items={items}
            getKey={(contact) => contact.id}
            defaultSort={{ label: "Contact", direction: "ascending" }}
            columns={[
              {
                label: "Contact",
                sortValue: (contact) => contact.displayName.toLowerCase(),
                render: (contact) => (
                  <Link className="button ghost small" to={`/contacts/${contact.id}`}>
                    <UserRound size={16} aria-hidden="true" /> {contact.displayName}
                  </Link>
                ),
              },
              {
                // The person and the organisation they act for are two different facts, and
                // the reason staff pick up the phone knowing who they are calling. An
                // individual acts for nobody, which has to read differently from an
                // organisation whose name was never captured.
                label: "Acting for",
                sortValue: (contact) => contact.organisation.toLowerCase(),
                render: (contact) =>
                  contact.organisation ? (
                    <>
                      <strong>{contact.organisation}</strong>
                      <br />
                      <small>{contact.roleTitle || "No role recorded"}</small>
                    </>
                  ) : contact.type === "individual" ? (
                    <span className="muted">Themselves</span>
                  ) : (
                    <span className="muted">Not recorded</span>
                  ),
              },
              {
                label: "Type",
                sortValue: (contact) => contact.type,
                render: (contact) => contactTypeLabels[contact.type] ?? contact.type,
              },
              {
                label: "Email",
                sortValue: (contact) => contact.email,
                render: (contact) => contact.email || "Not available",
              },
              { label: "Phone", render: (contact) => contact.phone || "Not available" },
              {
                label: "Status",
                sortValue: (contact) => contact.status,
                render: (contact) => <StatusPill value={contact.status} />,
              },
              {
                label: "Portal",
                render: (contact) => (
                  <StatusPill value={contact.portalEnabled ? "enabled" : "disabled"} />
                ),
              },
              {
                label: "Added",
                sortValue: (contact) => contact.createdAt,
                render: (contact) => formatDate(contact.createdAt),
              },
            ]}
          />
        ) : (
          <EmptyState
            title="No contacts found"
            description="Add a contact, clear the search, or choose a different status."
          />
        )}
      </Card>
    </>
  );
}
